import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { userService } from '../../../services/userService';
import { hierarchyService } from '../../../services/hierarchyService';

const AddEditUserModal = ({ isOpen, onClose, user, onSave }) => {
    const FALLBACK_ROLES = [
        'system_administrator', 'chief_principal', 'vice_chancellor', 'deputy_principal_academic',
        'deputy_principal_administration', 'registrar_academic', 'dean_of_faculty',
        'head_of_department', 'head_of_section', 'poe_manager', 'timetable_master',
        'trainer_lecturer', 'assessment_officer', 'quality_assurance_officer',
        'external_poe_verifier', 'finance_officer', 'procurement_officer',
        'stores_officer', 'dean_of_students', 'student'
    ].map(role => ({ id: role, role }));
    const [formData, setFormData] = React.useState({
        full_name: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        personal_email: '',
        email: '',
        primary_role: 'trainer_lecturer',
        student_id: '',
        employee_id: '',
        department_id: '',
        secondary_roles: '',
        status: 'active'
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [availableRoles, setAvailableRoles] = React.useState([]);
    const [departments, setDepartments] = React.useState([]);
    const [roleQuery, setRoleQuery] = React.useState('');
    const [roleMenuOpen, setRoleMenuOpen] = React.useState(false);

    const roleOptions = React.useMemo(() => {
        const fetchedRoles = Array.isArray(availableRoles) && availableRoles.length
            ? availableRoles
            : FALLBACK_ROLES;
        return fetchedRoles;
    }, [availableRoles]);
    const selectedRole = roleOptions.find(roleItem => roleItem.role === formData.primary_role);
    const filteredRoles = roleOptions.filter(roleItem => {
        const query = roleQuery.trim().toLowerCase();
        if (!query) return true;
        return `${roleItem.role} ${roleItem.label || ''}`.toLowerCase().includes(query);
    });
    const formatRoleLabel = (roleItem) => (roleItem?.label || roleItem?.role || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());

    React.useEffect(() => {
        const fetchRoles = async () => {
            try {
                const roles = await userService.getRoles();
                if (roles && roles.length > 0) {
                    setAvailableRoles(roles);
                } else {
                    console.warn("No roles found in database");
                }
            } catch (err) {
                console.error("Failed to fetch roles", err);
            }
        };
        fetchRoles();
        hierarchyService.getDepartments().then(setDepartments).catch(error => console.error('Failed to load departments', error));
    }, []);

    // Populate form data when user prop changes
    React.useEffect(() => {
        if (user) {
            const nameParts = String(user.full_name || '').trim().split(/\s+/);
            setFormData({
                full_name: user.full_name || '',
                first_name: user.first_name || nameParts[0] || '',
                middle_name: user.middle_name || (nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : ''),
                last_name: user.last_name || nameParts[nameParts.length - 1] || '',
                personal_email: user.personal_email || '',
                email: user.email || '',
                primary_role: user.primary_role || 'trainer_lecturer',
                student_id: user.student_id || user.student_ID || '',
                employee_id: user.employee_id || '',
                department_id: user.department?.id || user.staff?.department_id || user.department_id || '',
                secondary_roles: user.secondary_roles || [],
                status: user.status || 'active'
            });
        } else {
            // Reset form for new user
            setFormData({
                full_name: '',
                first_name: '', middle_name: '', last_name: '', personal_email: '',
                email: '',
                primary_role: 'trainer_lecturer',
                student_id: '',
                employee_id: '',
                department_id: '',
                secondary_roles: [],
                status: 'active'
            });
        }
    }, [user]);

    const [error, setError] = React.useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            // Ensure secondary_roles is sent as an ARRAY to the backend
            const payload = {
                ...formData,
                full_name: [formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(' '),
                secondary_roles: Array.isArray(formData.secondary_roles)
                    ? formData.secondary_roles
                    : String(formData.secondary_roles).split(',').map(r => r.trim()).filter(Boolean)
            };

            await onSave(payload);
            onClose();
        } catch (err) {
            console.error('Error submitting user:', err);
            setError(err.message || 'Failed to save user. Ensure the Edge Function is deployed.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-background rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto border animate-in zoom-in-95 duration-200">
                <div className="p-6 space-y-6">
                    <div className="flex justify-between items-center border-b pb-4">
                        <h3 className="text-xl font-bold">{user ? 'Edit User' : 'Add New User'}</h3>
                        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">✕</button>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            <strong>Failed to save user:</strong> {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">First Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.first_name}
                                    onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                    placeholder="e.g. John"
                                />
                            </div>
                            <div className="space-y-2"><label className="text-sm font-medium">Middle Name</label><input type="text" className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={formData.middle_name} onChange={e => setFormData({ ...formData, middle_name: e.target.value })} placeholder="Optional" /></div>
                            <div className="space-y-2 col-span-2"><label className="text-sm font-medium">Surname</label><input type="text" required className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} placeholder="e.g. Mwangi" /></div>

                            <div className="space-y-2 col-span-2">
                                <label className="text-sm font-medium">Backup Personal Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.personal_email}
                                    onChange={e => setFormData({ ...formData, personal_email: e.target.value })}
                                    placeholder="e.g. john@gmail.com"
                                />
                                <p className="text-xs text-muted-foreground">The official staff email is generated automatically from the surname and configured staff domain.</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Primary Role</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        role="combobox"
                                        aria-expanded={roleMenuOpen}
                                        aria-controls="primary-role-options"
                                        required
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                        value={roleMenuOpen ? roleQuery : formatRoleLabel(selectedRole || { role: formData.primary_role })}
                                        onFocus={() => { setRoleMenuOpen(true); setRoleQuery(''); }}
                                        onChange={e => { setRoleQuery(e.target.value); setRoleMenuOpen(true); }}
                                        onBlur={() => setTimeout(() => setRoleMenuOpen(false), 120)}
                                        placeholder="Type to filter roles..."
                                    />
                                    {roleMenuOpen && (
                                        <div id="primary-role-options" className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-background py-1 shadow-lg">
                                            {filteredRoles.length ? filteredRoles.map(roleItem => (
                                                <button
                                                    key={roleItem.id || roleItem.role}
                                                    type="button"
                                                    onMouseDown={event => event.preventDefault()}
                                                    onClick={() => {
                                                        setFormData({ ...formData, primary_role: roleItem.role });
                                                        setRoleQuery('');
                                                        setRoleMenuOpen(false);
                                                    }}
                                                    className={`block w-full px-3 py-2 text-left text-sm hover:bg-accent ${roleItem.role === formData.primary_role ? 'bg-accent text-primary' : ''}`}
                                                >
                                                    {formatRoleLabel(roleItem)}
                                                </button>
                                            )) : (
                                                <p className="px-3 py-2 text-sm text-muted-foreground">No matching roles</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Conditional ID Field */}
                            {formData.primary_role === 'student' ? (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Admission Number</label>
                                    <input
                                        type="text"
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                        value={formData.student_id}
                                        onChange={e => setFormData({ ...formData, student_id: e.target.value })}
                                        placeholder="e.g. ADM/001/2024"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">PF Number</label>
                                    <input
                                        type="text"
                                        disabled
                                        className="w-full rounded-md border bg-muted px-3 py-2 text-sm opacity-70"
                                        value={formData.employee_id}
                                        placeholder="Generated automatically, e.g. 0001"
                                    />
                                </div>
                            )}

                            {formData.primary_role !== 'student' && (
                                <div className="space-y-2 col-span-2">
                                    <label className="text-sm font-medium">Mother Department <span className="text-red-600">*</span></label>
                                    <select
                                        required
                                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                        value={formData.department_id}
                                        onChange={e => setFormData({ ...formData, department_id: e.target.value })}
                                    >
                                        <option value="">Select the staff member's permanent department</option>
                                        {departments.map(department => <option key={department.id} value={department.id}>{department.name} ({department.code})</option>)}
                                    </select>
                                    <p className="text-xs text-muted-foreground">Cross-department teaching requires approval from this mother department's HOD.</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Status</label>
                                <select
                                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="deactivated">Deactivated</option>
                                </select>
                            </div>

                            <div className="space-y-2 col-span-2">
                                <label className="text-sm font-medium">Secondary Roles (Optional)</label>
                                {roleOptions.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-muted/20">
                                        {roleOptions
                                            .filter(r => r.role !== formData.primary_role)
                                            .map(roleItem => {
                                                const getRolesArray = (val) => {
                                                    if (!val) return [];
                                                    if (Array.isArray(val)) return val;
                                                    return String(val).split(',').map(r => r.trim());
                                                };

                                                const currentRolesList = getRolesArray(formData.secondary_roles);
                                                const roleValue = roleItem.role;
                                                const isChecked = currentRolesList.includes(roleValue);
                                                const label = formatRoleLabel(roleItem);

                                                return (
                                                    <label key={roleValue} className="flex items-center space-x-2 text-sm cursor-pointer hover:text-primary transition-colors">
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                const currentRoles = getRolesArray(formData.secondary_roles);
                                                                let newRoles;
                                                                if (e.target.checked) {
                                                                    newRoles = [...currentRoles, roleValue];
                                                                } else {
                                                                    newRoles = currentRoles.filter(r => r !== roleValue);
                                                                }
                                                                setFormData({ ...formData, secondary_roles: newRoles });
                                                            }}
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                );
                                            })}
                                    </div>
                                ) : (
                                    <div className="p-3 text-sm text-muted-foreground border rounded-md bg-muted/10">
                                        Loading roles or no roles available...
                                    </div>
                                )}
                                <p className="text-xs text-muted-foreground">Select additional roles held by this user.</p>
                            </div>
                        </div>

                        <div className="pt-6 flex gap-3 justify-end border-t mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-md border transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-sm disabled:opacity-50 transition-colors"
                            >
                                {isSubmitting ? 'Saving...' : 'Save User'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddEditUserModal;
